import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { updateOnboardingSession } from '../../../../services/wizardApi';
import { toast } from 'react-toast';
import { LuDownload, LuCheck, LuLoader, LuInfo, LuRefreshCw, LuChrome } from 'react-icons/lu';
import '../../../../styles/wizard.css';
import { useDownloadAgent } from '../../../../hooks/API/wizard.api';
import { useAgentConnection } from '../../../../hooks/useAgentConnection';
import { STEP_STATUS, TEST_CREATION_MODES } from '../../../../Config/Onboarding/constant';
import { getUIDiscoveryStepNumbers } from '../../../../Config/Onboarding/stepFlow';
import axiosInstance from '../../../../api/axiosInstance';

const COMING_SOON_ASTERISK = (
  <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '0.25rem', cursor: 'help' }} title="Coming Soon">*</span>
);

const FEATURES = [
  { icon: '🤖', label: 'Browser Control' },
  { icon: '🎥', label: 'Test Recording' },
  { icon: '📝', label: 'Script Generation' },
];

const INSTALL_STEPS = [
  { label: '2. Run Installer',     desc: 'Installs agent + Chrome extension automatically' },
  { label: '3. Start Agent',       desc: 'Runs in system tray, enables extension' },
  { label: '4. Verify Connection', desc: 'Click button below to check status' },
];



const Step6BInstallAgentExtension = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { nextStep, previousStep, sessionId, markStepCompleted, setStepStatus, state, engine, testCreationMode } = useOnboarding();
  const { mutate } = useDownloadAgent();

  // UI Discovery reuses this screen as "Setup Tools" (step 7, between AI Preview
  // and Discovery Setup). The recorder flow keeps its original step-6 behaviour.
  const effectiveMode = searchParams.get('mode') || testCreationMode;
  const isUIDiscoveryMode =
    effectiveMode === 'ui' || effectiveMode === TEST_CREATION_MODES.UI_DISCOVERY;
  const discoverySteps = getUIDiscoveryStepNumbers(effectiveMode);

  const currentStepNumber = isUIDiscoveryMode ? discoverySteps.setupTools : 6;
  const stepperCompletedSteps = isUIDiscoveryMode
    ? [1, 2, 3, 4, 5, discoverySteps.aiPreview]
    : [1, 2, 3, 4, 5];

  // Save wizard data when component renders.
  // UI Discovery already persists its wizard data earlier in the flow, so this
  // call is skipped there and only the recorder flow hits the API.
  useEffect(() => {
    if (isUIDiscoveryMode) return;

    const saveWizardData = async () => {
      try {
        await axiosInstance.post('/wizard/add_wizard_data', {
          wizard_data: {
            engine: engine,
            data: state.data
          }
        });
        console.log('[Step 6B] Wizard data saved successfully');
      } catch (error) {
        console.warn('[Step 6B] Warning: Failed to save wizard data:', error.message);
      }
    };

    if (engine && state?.data) {
      saveWizardData();
    }
  }, []);


    const proceedToNextStep = useCallback(async () => {
    const nextStepNumber = isUIDiscoveryMode ? discoverySteps.setup : 7;
    markStepCompleted(currentStepNumber);
    setStepStatus(nextStepNumber, STEP_STATUS.IN_PROGRESS);
    if (sessionId) {
      await updateOnboardingSession(sessionId, {
        currentStep: isUIDiscoveryMode ? currentStepNumber : '6B',
        recorder: { agentConnected: true, extensionConnected: true },
      });
    }
    nextStep();
    navigate(
      isUIDiscoveryMode
        ? `/app/onboarding?step=${nextStepNumber}&mode=${discoverySteps.modeQuery}`
        : '/app/onboarding?step=7&mode=recorder'
    );
  }, [sessionId, markStepCompleted, setStepStatus, nextStep, navigate, isUIDiscoveryMode, currentStepNumber, discoverySteps.setup, discoverySteps.modeQuery]);

  const {
    isChecking,
    agentConnected,
    extensionConnected,
    agentVersion,
    extensionVersion,
    handleCheckConnection,
  } = useAgentConnection(proceedToNextStep);

  const bothConnected = agentConnected && extensionConnected;

  const handleContinue = async () => {
    if (!bothConnected) {
      toast.error('Please install the agent and verify connection');
      return;
    }
    try {
      await proceedToNextStep();
    } catch {
      toast.error('Failed to proceed');
    }
  };

  const handleLinuxDownload = () => {
    mutate(undefined, {
      onSuccess: ({ download_url }) => {
        const link = document.createElement('a');
        link.href = encodeURI(download_url);
        link.setAttribute('download', '');
        document.body.appendChild(link);
        link.click();
        link.remove();
      },
      onError: () => toast.error('Linux download failed'),
    });
  };

  const handleDownload = (os) => {
  mutate(
    { os, agent_type: 1, device_name: '' },
    {
      onSuccess: (response) => {
        const downloadUrl = response?.agent_path || response?.agent_path;
        if (downloadUrl) {
          const link = document.createElement('a');
          link.href = encodeURI(downloadUrl);
          link.setAttribute('download', '');
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          toast.error('Download URL not found');
        }
      },
      onError: () => toast.error(`${os} download failed`),
    }
  );
};

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={currentStepNumber} completedSteps={stepperCompletedSteps} />

        <WizardCard
          title="Install Robitest Agent"
          subtitle="Includes browser automation and Chrome extension"
        >
          {/* Status Bar */}
          <div style={{
            padding: '0.875rem 1rem',
            backgroundColor: bothConnected ? 'var(--success-color-light)' : 'var(--card-bg)',
            borderRadius: '8px',
            border: `1px solid ${bothConnected ? 'var(--success-color)' : 'var(--border-color)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {bothConnected
                ? <LuCheck size={18} style={{ color: 'var(--success-color)' }} />
                : <LuInfo size={18} style={{ color: 'var(--text-secondary)' }} />}
              <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {bothConnected
                  ? (isUIDiscoveryMode ? 'Ready to Discover' : 'Ready to Record')
                  : 'Installation Required'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                {agentConnected
                  ? <LuCheck size={14} style={{ color: 'var(--success-color)' }} />
                  : <span>○</span>}
                <span>Agent {agentVersion}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                {extensionConnected
                  ? <LuCheck size={14} style={{ color: 'var(--success-color)' }} />
                  : <span>○</span>}
                <LuChrome size={12} />
                <span>Extension {extensionVersion}</span>
              </div>
            </div>
          </div>

          {/* Download + Install Steps */}
          {!bothConnected && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '1rem', alignItems: 'center', marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>1. Download Agent</div>
             <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  className="wizard-btn wizard-btn-secondary"
                  disabled
                  title="Coming Soon"
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                >
                  <LuDownload size={14} /> Windows{COMING_SOON_ASTERISK}
                </button>
                <button
                  type="button"
                  className="wizard-btn wizard-btn-secondary"
                  disabled
                  title="Coming Soon"
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                >
                  <LuDownload size={14} /> macOS{COMING_SOON_ASTERISK}
                </button>
                <button type="button" className="wizard-btn wizard-btn-secondary" onClick={() => handleDownload(1)}>
                  <LuDownload size={14} /> Linux
                </button>
              </div>
              </div>

              <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                {INSTALL_STEPS.map(({ label, desc }) => (
                  <div key={label} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '1rem', alignItems: 'center' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{desc}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Features */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {FEATURES.map(({ icon, label }) => (
              <div key={label} style={{ padding: '0.75rem', textAlign: 'center', backgroundColor: 'var(--card-bg)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{icon}</div>
                <div style={{ fontSize: '0.75rem', fontWeight: 500 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Check Connection */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1rem' }}>
        <button
          className="wizard-btn wizard-btn-primary"
          onClick={() => {
            handleCheckConnection();
            console.log('Checking connection...');
          }}
          disabled={isChecking}
          style={{ minWidth: '180px', height: '40px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          {isChecking ? (
            <><LuLoader className="spinner mt-3" size={4} /> Checking...</>
          ) : (
            <><LuRefreshCw size={16} /> Check Connection</>
          )}
        </button>
        <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '10px' }}>
          Need help? <a href="/support" style={{ color: '#3b82f6', textDecoration: 'none' }}>Contact Support</a>
        </p>
      </div>

          <WizardActions
            showBack showNext
            onBack={() => {
              previousStep();
              navigate(
                isUIDiscoveryMode
                  ? `/app/onboarding?step=${discoverySteps.aiPreview}&mode=${discoverySteps.modeQuery}`
                  : '/app/onboarding?step=5'
              );
            }}
            onNext={handleContinue}
            nextLabel={isUIDiscoveryMode ? 'Continue to Discovery' : 'Continue to Recording'}
            nextDisabled={!bothConnected}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step6BInstallAgentExtension;